import React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../components/ui/Modal/Modal';
import { useUIStore } from '../stores/uiStore';
import { SearchModal } from './features/SearchModal/SearchModal';
import { Bell, ClipboardList, Play, Wifi } from 'lucide-react';

export const ModalManager: React.FC = () => {
  const { t } = useTranslation();
  const { activeModal, modalData, closeModal } = useUIStore();

  if (!activeModal) return null;

  // WiFi Modal
  if (activeModal === 'wifi') {
    return (
      <Modal isOpen={true} onClose={closeModal} title={t('home.quickActions.wifi')} size="sm">
        <div className="text-center py-4">
          <div className="w-20 h-20 rounded-full bg-[#FDF6E9] flex items-center justify-center mx-auto mb-4">
            <Wifi className="w-10 h-10 text-[#D4A054]" />
          </div>
          <h3 className="text-lg font-medium text-[#333333] mb-2">{t('modals.wifi.title')}</h3>
          <div className="bg-[#F5F5F5] rounded-xl p-4 mb-4">
            <p className="text-sm text-[#666666] mb-1">{t('modals.wifi.network')}</p>
            <p className="text-lg font-medium text-[#333333]">Leela-Guest</p>
          </div>
          <div className="bg-[#F5F5F5] rounded-xl p-4">
            <p className="text-sm text-[#666666] mb-1">{t('modals.wifi.password')}</p>
            <p className="text-lg font-medium text-[#333333]">Welcome2026!</p>
          </div>
        </div>
      </Modal>
    );
  }

  // Reception Modal
  if (activeModal === 'reception') {
    return (
      <Modal isOpen={true} onClose={closeModal} title="Reception" size="sm">
        <div className="text-center py-4">
          <div className="w-20 h-20 rounded-full bg-[#FDF6E9] flex items-center justify-center mx-auto mb-4">
            <Bell className="w-10 h-10 text-[#D4A054]" />
          </div>
          <h3 className="text-lg font-medium text-[#333333] mb-4">Contact Reception</h3>
          <div className="space-y-3">
            <button className="w-full py-3 px-4 bg-[#D4A054] text-white rounded-xl font-medium hover:bg-[#B8893F] transition-colors">
              Call Reception (Dial 0)
            </button>
            <button className="w-full py-3 px-4 bg-[#F5F5F5] text-[#333333] rounded-xl font-medium hover:bg-[#E0E0E0] transition-colors">
              Send Message
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  // Room Service Modal
  if (activeModal === 'room-service') {
    return (
      <Modal isOpen={true} onClose={closeModal} title="Room Service" size="md">
        <div className="py-2">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-full bg-[#FDF6E9] flex items-center justify-center">
              <ClipboardList className="w-8 h-8 text-[#D4A054]" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-[#333333]">24/7 Room Service</h3>
              <p className="text-sm text-[#666666]">Dial 1 from your room phone</p>
            </div>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-[#F8F8F8] rounded-xl">
              <span className="text-[#333333]">Breakfast</span>
              <span className="text-[#666666] text-sm">6:00 AM - 10:30 AM</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-[#F8F8F8] rounded-xl">
              <span className="text-[#333333]">All Day Dining</span>
              <span className="text-[#666666] text-sm">11:00 AM - 11:00 PM</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-[#F8F8F8] rounded-xl">
              <span className="text-[#333333]">Night Menu</span>
              <span className="text-[#666666] text-sm">11:00 PM - 6:00 AM</span>
            </div>
          </div>

          <button className="w-full mt-6 py-3 px-4 bg-[#D4A054] text-white rounded-xl font-medium hover:bg-[#B8893F] transition-colors">
            View Full Menu
          </button>
        </div>
      </Modal>
    );
  }

  // Room Tour Modal
  if (activeModal === 'tour') {
    return (
      <Modal isOpen={true} onClose={closeModal} title="Room Tour" size="lg">
        <div className="py-2">
          <div className="relative rounded-2xl overflow-hidden mb-4 bg-[#F5F5F5]">
            <div className="aspect-video flex items-center justify-center">
              <div className="text-center">
                <Play className="w-16 h-16 text-[#D4A054] mx-auto mb-2" />
                <p className="text-[#666666]">Video tour coming soon</p>
              </div>
            </div>
          </div>
          <p className="text-[#666666] text-sm leading-relaxed">
            Take a virtual tour of your luxury suite. Explore all amenities, room features, 
            and learn how to use the room controls. This interactive guide will help you 
            make the most of your stay at Leela Palace.
          </p>
        </div>
      </Modal>
    );
  }

  // Amenity Detail Modal
  if (activeModal === 'amenity-detail' && modalData) {
    const amenity = modalData as { name: string; description: string; icon?: React.ReactNode };
    return (
      <Modal isOpen={true} onClose={closeModal} title={amenity.name} size="md">
        <div className="py-4 text-center">
          <div className="w-24 h-24 rounded-full bg-[#FDF6E9] flex items-center justify-center mx-auto mb-4">
            {amenity.icon || <div className="text-4xl">✨</div>}
          </div>
          <p className="text-[#666666] leading-relaxed">
            {amenity.description}
          </p>
          <button 
            onClick={closeModal}
            className="mt-6 px-8 py-3 bg-[#D4A054] text-white rounded-xl font-medium hover:bg-[#B8893F] transition-colors"
          >
            Got it
          </button>
        </div>
      </Modal>
    );
  }

  // Restaurant Menu Modal
  if (activeModal === 'restaurant-menu' && modalData) {
    const restaurant = modalData as { name: string; cuisine: string };
    return (
      <Modal isOpen={true} onClose={closeModal} title={restaurant.name} size="md">
        <div className="py-2">
          <p className="text-[#666666] mb-4">{restaurant.cuisine}</p>
          <div className="space-y-2">
            <div className="p-3 bg-[#F8F8F8] rounded-xl">
              <h4 className="font-medium text-[#333333]">Breakfast</h4>
              <p className="text-sm text-[#666666]">7:00 AM - 10:30 AM</p>
            </div>
            <div className="p-3 bg-[#F8F8F8] rounded-xl">
              <h4 className="font-medium text-[#333333]">Lunch</h4>
              <p className="text-sm text-[#666666]">12:00 PM - 3:00 PM</p>
            </div>
            <div className="p-3 bg-[#F8F8F8] rounded-xl">
              <h4 className="font-medium text-[#333333]">Dinner</h4>
              <p className="text-sm text-[#666666]">7:00 PM - 11:00 PM</p>
            </div>
          </div>
          <button className="w-full mt-4 py-3 px-4 bg-[#D4A054] text-white rounded-xl font-medium hover:bg-[#B8893F] transition-colors">
            Reserve Table
          </button>
        </div>
      </Modal>
    );
  }
  // Search Modal
  if (activeModal === 'search') {
    return <SearchModal onClose={closeModal} />;
  }

  return null;
};

export default ModalManager;
